import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

export const SearchableOwnerSelect = ({ 
  owners = [], 
  value = '', 
  onValueChange = () => {},
  placeholder = "Select owner",
  disabled = false,
  className = "",
  testId = "owner-select"
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const triggerRef = useRef(null);

  const selectedOwner = owners.find(owner => owner.id === value);
  
  const filteredOwners = search === '' 
    ? owners 
    : owners.filter(owner =>
        owner.full_name.toLowerCase().includes(search.toLowerCase())
      );

  const handleSelect = (ownerId) => {
    onValueChange(ownerId);
    setOpen(false);
    setSearch('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between rounded-lg border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-3 py-2",
            !value && "text-muted-foreground",
            className
          )}
          data-testid={testId}
        >
          <span className="truncate">
            {selectedOwner ? selectedOwner.full_name : placeholder}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="p-0 shadow-lg" 
        align="start"
        side="bottom"
        style={{ 
          width: triggerRef.current ? triggerRef.current.offsetWidth : 'auto' 
        }}
      >
        <Command shouldFilter={false} className="w-full">
          <CommandInput 
            placeholder="Search owners..." 
            value={search}
            onValueChange={setSearch}
            className="text-sm focus:outline-none focus:ring-0"
          />
          <CommandList className="max-h-[200px]">
            {filteredOwners.length === 0 ? (
              <CommandEmpty>No owner found.</CommandEmpty>
            ) : (
              <CommandGroup>
                {filteredOwners.map((owner) => (
                  <CommandItem
                    key={owner.id}
                    value={owner.id}
                    onSelect={() => handleSelect(owner.id)}
                    className="cursor-pointer hover:bg-accent"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4 flex-shrink-0",
                        value === owner.id ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <span className="truncate flex-1">{owner.full_name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
